import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPhone, FiCalendar, FiChevronRight } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { formatTimestamp } from '../../../../../utils/timestampFormatter';
import { TIMEZONES } from '../../../../../utils/consts';
import { localeByLanguage } from '../careProfileAssets';
import { CareTeamMember, CareProfileTierFlags } from 'data-types';
import CareProfileCard from './CareProfileCard';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import { getAskADoctorPagePath } from '../../../../../utils/helpers';

/** Runtime shape for chatTarget (supports both ask-a-doctor path and WhatsApp). */
type ChatTarget =
	| { kind: 'ask-a-doctor'; path: string }
	| { kind: 'whatsapp' }
	| { path: string };

/** CareTeamMember may lag behind data-types rebuild in the webpack typechecker. */
type CareTeamMemberView = CareTeamMember & {
	earliestSlotStart?: string | null;
	chatTarget?: ChatTarget | null;
};

/** Tier flags may lag behind data-types when webpack still holds a cached CareProfileTierFlags. */
type CareProfileTierFlagsView = CareProfileTierFlags & {
	doctorVideoConsultationEnabled?: boolean;
	/** @deprecated old name — prefer doctorVideoConsultationEnabled */
	videoConsultationEnabled?: boolean;
};

interface CareTeamCardsProps {
	id?: string;
	members: CareTeamMember[];
	tierFlags: CareProfileTierFlagsView;
	language: string;
	country?: string;
	onMessage?: (member: CareTeamMember) => void;
	onBook?: (member: CareTeamMember) => void;
}

const formatAppointment = (date: string, language: string, country?: string): string => {
	const timeZone =
		TIMEZONES[(country ?? '').toUpperCase() as keyof typeof TIMEZONES] ?? 'Europe/Berlin';
	return formatTimestamp(date, {
		showYear: false,
		showTime: true,
		use24Hour: true,
		locale: localeByLanguage[language] || 'en-US',
		timeZone,
	});
};

const roleLabelKey = (role: CareTeamMember['role']) => (role === 'doctor' ? 'CareProfile/CareTeam/Doctor' : 'CareProfile/CareTeam/Nutritionist');
const descriptionKey = (role: CareTeamMember['role']) => (role === 'doctor' ? 'CareProfile/CareTeam/DoctorDescription' : 'CareProfile/CareTeam/NutritionistDescription');
const messageLabelKey = (role: CareTeamMember['role']) => (role === 'doctor' ? 'CareProfile/CareTeam/MessageDoctor' : 'CareProfile/CareTeam/MessageNutritionist');

const getChatTargetKind = (target: ChatTarget | null | undefined): 'whatsapp' | 'ask-a-doctor' | null => {
	if (!target) return null;
	if ('kind' in target && target.kind === 'whatsapp') return 'whatsapp';
	if ('kind' in target && target.kind === 'ask-a-doctor') return 'ask-a-doctor';
	// Legacy shape: { path: string }
	if ('path' in target && target.path) return 'ask-a-doctor';
	return null;
};

const getChatTargetPath = (target: ChatTarget | null | undefined): string | null => {
	if (!target) return null;
	if ('path' in target && typeof target.path === 'string') return target.path;
	return null;
};

/** Contact-us DE number — used when the contact translation isn't loaded yet. */
const DEFAULT_WHATSAPP_NUMBER = '493052015208';

const openWhatsAppForDoctor = (language: string): boolean => {
	// Number + prefilled message live in the contact translation namespace (same as Contact Us).
	const number = translator.translate(language, 'contact', 'whatsapp_link_number');
	const message = translator.translate(language, 'my_account', 'CareProfile/CareTeam/WhatsAppPrefill');
	const fallbackMessage = translator.translate(language, 'contact', 'whatsapp_prefilled_message');
	const resolvedNumber =
		typeof number === 'string' && number && !number.includes('whatsapp_link_number')
			? number
			: DEFAULT_WHATSAPP_NUMBER;
	const resolvedMessage =
		typeof message === 'string' && message && !message.includes('CareProfile/CareTeam/WhatsAppPrefill')
			? message
			: (typeof fallbackMessage === 'string' && fallbackMessage && !fallbackMessage.includes('whatsapp_prefilled_message')
					? fallbackMessage
					: 'Hello, I have a question about my Care+ treatment.');
	try {
		// openWhatsAppChat uses window.open; a blocked popup returns null → treat as failure.
		const msg = encodeURIComponent(resolvedMessage);
		const popup = window.open(`https://wa.me/${resolvedNumber}?text=${msg}`, '_blank');
		if (!popup) {
			window.clientLogger?.warn?.('CareTeamCards: WhatsApp popup blocked');
			return false;
		}
		return true;
	} catch (err) {
		window.clientLogger?.error?.('CareTeamCards: failed to open WhatsApp', err);
		return false;
	}
};

const CareTeamCards: React.FC<CareTeamCardsProps> = ({ id = 'care-team-cards', members, tierFlags, language, country, onMessage, onBook }) => {
	const navigate = useNavigate();

	const openAskADoctor = (member: CareTeamMember) => {
		if (onMessage) {
			onMessage(member);
			return;
		}
		const view = member as CareTeamMemberView;
		const path = getChatTargetPath(view.chatTarget);
		if (path) {
			navigate(path);
			return;
		}
		// Last-resort fallback so the CTA never appears dead (the route is per country: ask-a-prescriber in the UK).
		navigate(`/${country ?? language}/account/${getAskADoctorPagePath(country)}`);
	};

	const handleMessage = (member: CareTeamMember) => {
		// All roles/tiers message through the in-app ask-a-doctor chat: the patient uses the
		// dashboard chat sheet, the doctor replies from doc-panel, and both sides see the thread.
		const view = member as CareTeamMemberView;
		const kind = getChatTargetKind(view.chatTarget);
		const channel = kind === 'whatsapp' && member.role === 'doctor' ? 'whatsapp' : onMessage ? 'chat' : 'go_to';
		MainTracker.track('custom', 'Contact Care Team', { provider_type: member.role, channel });
		// Entitled doctor → WhatsApp (GLP-1 pool). Fall back to in-app chat if WA fails.
		if (kind === 'whatsapp' && member.role === 'doctor') {
			const opened = openWhatsAppForDoctor(language);
			if (!opened) openAskADoctor(member);
			return;
		}
		// Specialist / basic doctor → standard ask-a-doctor chat sheet or route.
		openAskADoctor(member);
	};

	return (
		<CareProfileCard id={id} className="flex flex-col gap-y-4">
			<div>
				<p className="text-[12px] font-medium text-[#f59301] uppercase tracking-[2px] leading-[16px]">{translator.translate(language, 'my_account', 'CareProfile/CareTeam/Eyebrow')}</p>
				{/* Product copy: "your care team" — no named/dedicated practitioner in the title. */}
				<p className="text-[17px] font-medium text-care-plus-heading mt-2">{translator.translate(language, 'my_account', 'CareProfile/CareTeam/Title')}</p>
				<p className="text-sm text-[#616975] mt-1 leading-[1.2]">{translator.translate(language, 'my_account', 'CareProfile/CareTeam/Body')}</p>
			</div>

			{members.map((member) => {
				const view = member as CareTeamMemberView;
				const isDoctor = member.role === 'doctor';
				// Prefer role labels ("your GLP-1 doctor" / "your care team") over a named doctor.
				const heading = translator.translate(language, 'my_account', roleLabelKey(member.role));

				// Figma 3-state card:
				// 1) Booked     — bookedSession set → orange gradient + call row
				// 2) Available  — eligible now (nextEligibleAt <= now) → cream + available pill
				// 3) Default    — otherwise cream + message CTA only (no pill)
				const isBooked = Boolean(member.bookedSession);
				// Server: nextEligibleAt null = eligible now. Only a future date means "not yet".
				const isEligibleNow =
					!member.nextEligibleAt ||
					new Date(member.nextEligibleAt as string).getTime() <= Date.now();
				const showAvailablePill = !isBooked && member.available && isEligibleNow;
				const showOnlineDot = !isBooked && member.available;
				const earliestSlotStart = view.earliestSlotStart ?? null;
				const earliestSlotLabelRaw = translator.translate(language, 'my_account', 'CareProfile/CareTeam/EarliestSlot');
				// Prefer localized CareTeam/EarliestSlot; fall back to Booking copy, then EN (HL-11173).
				const earliestSlotBookingRaw = translator.translate(language, 'my_account', 'CareProfile/Booking/EarliestSlot');
				const earliestSlotLabel =
					(typeof earliestSlotLabelRaw === 'string' && earliestSlotLabelRaw && !earliestSlotLabelRaw.includes('EarliestSlot'))
						? earliestSlotLabelRaw
						: (typeof earliestSlotBookingRaw === 'string' && earliestSlotBookingRaw && !earliestSlotBookingRaw.includes('EarliestSlot'))
							? earliestSlotBookingRaw
							: 'Earliest';

				return (
					<div
						key={member.role}
						id={`care-team-card-${member.role}`}
						data-card-state={isBooked ? 'booked' : showAvailablePill ? 'available' : 'default'}
						onClick={() => {
							if (showAvailablePill && onBook) { onBook(member); return; }
							if (member.chatTarget) handleMessage(member);
						}}
						className={`relative rounded-[14px] border border-solid border-[#dee3ed] overflow-hidden flex flex-col ${isBooked ? 'bg-care-plus-team-card-gradient' : 'bg-[#fcfbf9]'} ${!isBooked && !member.available ? 'opacity-60' : ''} ${member.chatTarget ? 'cursor-pointer' : ''}`}
					>
						<div className="p-4 pb-4 flex items-center gap-x-2">
							<div className="relative flex-shrink-0">
								<div className="relative w-[68px] h-[68px] rounded-full overflow-hidden border-2 border-solid border-white bg-white/20">
									<img
										src={member.photoUrl}
										alt={member.role}
										style={
											isDoctor
												? { position: 'absolute', width: '170%', height: '170%', left: '2px', top: '9px', transform: 'scaleX(-1)', transformOrigin: '30px 50%' }
												: { position: 'absolute', width: '170%', height: '170%', left: '-6px', top: '5px' }
										}
									/>
								</div>
								{showOnlineDot && <span className="absolute bottom-[3px] right-[3px] w-[15px] h-[15px] rounded-full bg-[#00c950] border-[3px] border-solid border-white" />}
							</div>
							<div className="flex flex-col gap-y-1 min-w-0">
								<p className={`text-[17px] font-medium leading-[1.3] break-words ${isBooked ? 'text-white' : 'text-care-plus-heading'}`}>{heading}</p>
								<p className={`text-[11px] leading-snug ${isBooked ? 'text-white/90' : 'text-[#616975]'}`}>
									{!member.available && member.backupNote ? member.backupNote : translator.translate(language, 'my_account', descriptionKey(member.role))}
								</p>
							</div>
						</div>

						{isBooked && (
							<div className="px-4 pb-4">
								<div
									id={`care-team-card-${member.role}-booked-session`}
									className="flex items-center justify-between gap-x-2 bg-white/90 rounded-[10px] px-3 py-2.5"
								>
									<div className="flex items-center gap-x-2.5 min-w-0">
										<span className="flex items-center justify-center w-8 h-8 rounded-md bg-care-plus-button-gradient flex-shrink-0">
											<FiCalendar className="text-white" size={16} />
										</span>
										<div className="flex flex-col min-w-0">
											<p className="text-[11px] font-medium text-care-plus-label uppercase tracking-wide truncate">
												{translator.translate(language, 'my_account', 'CareProfile/CareTeam/CallBookedFor')}
											</p>
											<p className="text-[13px] font-medium text-care-plus-orange-dark truncate">
												{formatAppointment(member.bookedSession!.startAt, language, country)}
											</p>
										</div>
									</div>
									<span className="flex items-center justify-center w-7 h-7 rounded-full bg-white flex-shrink-0">
										<FiPhone className="text-care-plus-orange" size={14} />
									</span>
								</div>
							</div>
						)}

						{/* Doctor video booking CTA gated by enable_care_plus_video_consultation; nutritionist not gated. */}
						{showAvailablePill && (member.role !== 'doctor' || (tierFlags.doctorVideoConsultationEnabled ?? tierFlags.videoConsultationEnabled)) && (
							<div className="px-4 pb-4">
								<button
									id={`care-team-card-${member.role}-available-pill`}
									type="button"
									onClick={(e) => { e.stopPropagation(); onBook && onBook(member); }}
									disabled={!onBook}
									style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center' }}
									className="gap-x-2 w-full min-w-0 bg-white rounded-[10px] px-3 py-2.5 border border-solid border-[#e7e4db] text-left cursor-pointer disabled:cursor-default"
								>
									<span style={{ flexShrink: 0 }} className="w-2.5 h-2.5 rounded-full bg-[#00c950]" />
									<div style={{ minWidth: 0 }} className="flex flex-col min-w-0">
										<p style={{ minWidth: 0, lineHeight: 1 }} className="text-[11px] font-medium text-care-plus-label uppercase tracking-wide leading-none truncate">
											{translator.translate(language, 'my_account', 'CareProfile/CareTeam/AvailableForConsultation')}
										</p>
										{earliestSlotStart && (
											<p className="text-[11px] text-[#616975] mt-0.5 truncate">
												{earliestSlotLabel}
												{': '}
												{formatAppointment(earliestSlotStart, language, country)}
											</p>
										)}
									</div>
								</button>
							</div>
						)}

						{member.chatTarget && (
							<button
								id={`care-team-card-${member.role}-message-button`}
								type="button"
								onClick={(e) => { e.stopPropagation(); handleMessage(member); }}
								className={
									isBooked
										? 'flex items-center justify-center gap-x-1 w-full bg-[#f9f7f1] rounded-b-[13px] text-[12px] font-medium text-[#f59708] py-[10px] border-none cursor-pointer hover:bg-[#f5efe4] transition-colors duration-200'
										: 'flex items-center justify-center gap-x-1 w-full bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover rounded-b-[13px] text-[12px] font-medium text-white py-[10px] transition-all duration-200 border-none cursor-pointer'
								}
							>
								{translator.translate(language, 'my_account', messageLabelKey(member.role))}
								<FiChevronRight size={12} />
							</button>
						)}
					</div>
				);
			})}

			{tierFlags.showNutritionistUpgradePrompt && (
				<div id="care-team-nutritionist-upgrade-prompt" className="w-full bg-[#f9f7f1] rounded-xl p-3">
					<p className="text-sm text-[#616975]">{translator.translate(language, 'my_account', 'CareProfile/CareTeam/UpgradePrompt')}</p>
				</div>
			)}
		</CareProfileCard>
	);
};

export default CareTeamCards;
